import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Select, DatePicker, Button, Space, Drawer,
  Typography, Descriptions, message, Tooltip, Image,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, PictureOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import request from '@/utils/request';
import { CosImage } from '@/components/CosImage';
import type {
  LLMCallLogItem, LLMCallLogDetail, LLMCallSummaryResponse, LLMCallLogListResponse,
  ChatMessage, ChatContentPart,
} from '@zero/shared';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const statusColor: Record<string, string> = {
  success: 'green',
  failed: 'red',
};

const costSourceColor: Record<string, string> = {
  upstream: 'green',
  table: 'blue',
  estimate: 'orange',
};

const costSourceLabel: Record<string, string> = {
  upstream: '厂商权威',
  table: '价目表',
  estimate: '估算',
};

function formatCost(value: string | null): string {
  if (value == null) return '—';
  const num = Number(value);
  if (!isFinite(num)) return '—';
  if (num === 0) return '$0';
  if (num < 0.0001) return `$${num.toExponential(2)}`;
  return `$${num.toFixed(6)}`;
}

const roleLabel: Record<string, string> = {
  system: '系统',
  user: '用户',
  assistant: '助手',
};

const roleColor: Record<string, string> = {
  system: 'default',
  user: 'blue',
  assistant: 'green',
};

const MessagePart: React.FC<{ part: ChatContentPart }> = ({ part }) => {
  if (part.type === 'text') {
    return (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{part.text ?? ''}</div>
    );
  }
  if (part.type === 'image_url' && part.image_url) {
    return <CosImage cosKey={part.image_url.cosKey} url={part.image_url.url} />;
  }
  return null;
};

const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message: m }) => {
  const role = m.role ?? 'unknown';
  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <Tag color={roleColor[role] || 'default'}>{roleLabel[role] || role}</Tag>
      </div>
      <div
        style={{
          padding: '8px 12px',
          background: '#f7f8fa',
          borderRadius: 6,
          border: '1px solid #f0f0f0',
        }}
      >
        {typeof m.content === 'string' ? (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</div>
        ) : Array.isArray(m.content) ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {m.content.map((p, i) => (
              <MessagePart key={i} part={p} />
            ))}
          </Space>
        ) : (
          <Typography.Text type="secondary">[空内容]</Typography.Text>
        )}
      </div>
    </div>
  );
};

const MessageList: React.FC<{ messages: unknown }> = ({ messages }) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return <Typography.Text type="secondary">未记录</Typography.Text>;
  }
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {(messages as ChatMessage[]).map((m, i) => (
        <MessageBubble key={i} message={m} />
      ))}
    </Space>
  );
};

const LLMCallLogPage: React.FC = () => {
  const [items, setItems] = useState<LLMCallLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<LLMCallSummaryResponse | null>(null);
  const [filters, setFilters] = useState<{
    provider?: string;
    status?: string;
    actorType?: string;
    range?: [Dayjs, Dayjs];
  }>({});
  const [detail, setDetail] = useState<LLMCallLogDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const res = await request.get<LLMCallSummaryResponse>('/admin/llm/calls/summary');
      setSummary(res.data);
    } catch (err) {
      message.error((err as Error)?.message || '加载汇总失败');
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, pageSize };
      if (filters.provider) params.provider = filters.provider;
      if (filters.status) params.status = filters.status;
      if (filters.actorType) params.actorType = filters.actorType;
      if (filters.range) {
        params.startAt = filters.range[0].startOf('day').toISOString();
        params.endAt = filters.range[1].endOf('day').toISOString();
      }
      const res = await request.get<LLMCallLogListResponse>('/admin/llm/calls', { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      message.error((err as Error)?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const openDetail = async (id: number) => {
    try {
      const res = await request.get<LLMCallLogDetail>(`/admin/llm/calls/${id}`);
      setDetail(res.data);
      setDetailOpen(true);
    } catch (err) {
      message.error((err as Error)?.message || '加载详情失败');
    }
  };

  const columns: ColumnsType<LLMCallLogItem> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', width: 70 },
      {
        title: '时间',
        dataIndex: 'createdAt',
        width: 170,
        render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: '调用方',
        width: 120,
        render: (_: unknown, r) => `${r.actorType}${r.actorId != null ? `#${r.actorId}` : ''}`,
      },
      { title: 'Provider', dataIndex: 'provider', width: 110 },
      { title: '模型', dataIndex: 'model', ellipsis: true },
      {
        title: '状态',
        dataIndex: 'status',
        width: 90,
        render: (v: string) => <Tag color={statusColor[v] || 'default'}>{v}</Tag>,
      },
      {
        title: 'Tokens',
        width: 140,
        render: (_: unknown, r) =>
          r.totalTokens != null
            ? `${r.promptTokens ?? '?'} / ${r.completionTokens ?? '?'} = ${r.totalTokens}`
            : '—',
      },
      {
        title: '费用 (USD)',
        width: 140,
        render: (_: unknown, r) => (
          <Space size={4}>
            <span>{formatCost(r.costUsd)}</span>
            {r.costSource && (
              <Tooltip title={r.costNote || costSourceLabel[r.costSource]}>
                <Tag color={costSourceColor[r.costSource] || 'default'} style={{ marginLeft: 4 }}>
                  {costSourceLabel[r.costSource] || r.costSource}
                </Tag>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: '附件',
        width: 60,
        render: (_: unknown, r) =>
          r.hasAttachments ? (
            <Tooltip title={`图片 ${r.attachmentsSummary?.imageCount ?? 0}`}>
              <PictureOutlined style={{ color: '#1677ff' }} />
            </Tooltip>
          ) : (
            '—'
          ),
      },
      {
        title: '延迟',
        dataIndex: 'latencyMs',
        width: 90,
        render: (v: number | null) => (v != null ? `${v}ms` : '—'),
      },
      {
        title: '错误',
        dataIndex: 'errorCode',
        width: 130,
        render: (v: string | null, r) =>
          v ? (
            <Tooltip title={r.errorMessage || v}>
              <Tag color="red">{v}</Tag>
            </Tooltip>
          ) : (
            '—'
          ),
      },
      {
        title: '操作',
        width: 80,
        fixed: 'right',
        render: (_: unknown, r) => <a onClick={() => openDetail(r.id)}>详情</a>,
      },
    ],
    [],
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>
        LLM 调用记录
      </Title>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic title="今日调用数" value={summary?.today.callCount ?? 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="今日费用 (USD)"
              value={formatCost(summary?.today.totalCostUsd ?? '0')}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="本月调用数" value={summary?.month.callCount ?? 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="本月费用 (USD)"
              value={formatCost(summary?.month.totalCostUsd ?? '0')}
            />
          </Card>
        </Col>
      </Row>

      {summary && summary.byProvider.length > 0 && (
        <Card title="按 Provider 汇总" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            {summary.byProvider.map((r) => (
              <Col span={8} key={r.key}>
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label="Provider">{r.key}</Descriptions.Item>
                  <Descriptions.Item label="调用数">
                    {r.callCount}（成功 {r.successCount} / 失败 {r.failedCount}）
                  </Descriptions.Item>
                  <Descriptions.Item label="Tokens">{r.totalTokens}</Descriptions.Item>
                  <Descriptions.Item label="费用 (USD)">{formatCost(r.totalCostUsd)}</Descriptions.Item>
                </Descriptions>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space wrap>
            <Select
              allowClear
              placeholder="Provider"
              style={{ width: 140 }}
              value={filters.provider}
              options={[
                { value: 'openrouter', label: 'OpenRouter' },
                { value: 'closeai', label: 'CloseAI' },
              ]}
              onChange={(v) => {
                setPage(1);
                setFilters((f) => ({ ...f, provider: v }));
              }}
            />
            <Select
              allowClear
              placeholder="状态"
              style={{ width: 120 }}
              value={filters.status}
              options={[
                { value: 'success', label: 'success' },
                { value: 'failed', label: 'failed' },
              ]}
              onChange={(v) => {
                setPage(1);
                setFilters((f) => ({ ...f, status: v }));
              }}
            />
            <Select
              allowClear
              placeholder="调用方类型"
              style={{ width: 140 }}
              value={filters.actorType}
              options={[
                { value: 'user', label: 'user' },
                { value: 'admin', label: 'admin' },
                { value: 'system', label: 'system' },
              ]}
              onChange={(v) => {
                setPage(1);
                setFilters((f) => ({ ...f, actorType: v }));
              }}
            />
            <RangePicker
              value={filters.range}
              onChange={(dates) => {
                setPage(1);
                setFilters((f) => ({
                  ...f,
                  range: dates && dates[0] && dates[1] ? [dates[0], dates[1]] : undefined,
                }));
              }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadList();
                loadSummary();
              }}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table<LLMCallLogItem>
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={items}
          columns={columns}
          scroll={{ x: 1400 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      <Drawer
        title={detail ? `调用详情 #${detail.id}` : ''}
        open={detailOpen}
        width={720}
        onClose={() => setDetailOpen(false)}
      >
        {detail && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Provider">{detail.provider}</Descriptions.Item>
              <Descriptions.Item label="模型">{detail.model}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={statusColor[detail.status] || 'default'}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="调用方">
                {detail.actorType}
                {detail.actorId != null ? `#${detail.actorId}` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Prompt tokens">{detail.promptTokens ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Completion tokens">
                {detail.completionTokens ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Total tokens">{detail.totalTokens ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="延迟">
                {detail.latencyMs != null ? `${detail.latencyMs}ms` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="费用 (USD)">{formatCost(detail.costUsd)}</Descriptions.Item>
              <Descriptions.Item label="费用来源">
                {detail.costSource ? (
                  <Tag color={costSourceColor[detail.costSource] || 'default'}>
                    {costSourceLabel[detail.costSource] || detail.costSource}
                  </Tag>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              {detail.costNote && (
                <Descriptions.Item label="费用备注" span={2}>
                  <Text type="secondary">{detail.costNote}</Text>
                </Descriptions.Item>
              )}
              {detail.generationId && (
                <Descriptions.Item label="Generation ID" span={2}>
                  <Text copyable code>
                    {detail.generationId}
                  </Text>
                </Descriptions.Item>
              )}
              {detail.errorCode && (
                <Descriptions.Item label="错误码" span={2}>
                  <Tag color="red">{detail.errorCode}</Tag>
                </Descriptions.Item>
              )}
              {detail.errorMessage && (
                <Descriptions.Item label="错误信息" span={2}>
                  <Paragraph type="danger" style={{ marginBottom: 0 }}>
                    {detail.errorMessage}
                  </Paragraph>
                </Descriptions.Item>
              )}
              {detail.attachmentsSummary && detail.hasAttachments && (
                <Descriptions.Item label="附件" span={2}>
                  <div style={{ marginBottom: 8 }}>
                    图片 {detail.attachmentsSummary.imageCount} 张 / 总大小{' '}
                    {Math.round((detail.attachmentsSummary.totalBytes || 0) / 1024)} KB
                  </div>
                  {detail.attachmentsSummary.cosKeys.length > 0 && (
                    <Image.PreviewGroup>
                      <Space wrap size={8}>
                        {detail.attachmentsSummary.cosKeys.map((k) => (
                          <CosImage key={k} cosKey={k} />
                        ))}
                      </Space>
                    </Image.PreviewGroup>
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Card
              title="请求消息"
              size="small"
              extra={
                <Button
                  size="small"
                  type="text"
                  onClick={() => {
                    navigator.clipboard
                      ?.writeText(JSON.stringify(detail.requestMessages, null, 2))
                      .then(() => message.success('已复制 JSON'))
                      .catch(() => message.error('复制失败'));
                  }}
                >
                  复制 JSON
                </Button>
              }
            >
              <MessageList messages={detail.requestMessages} />
            </Card>

            <Card title="响应内容" size="small">
              {detail.responseContent ? (
                <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                  {detail.responseContent}
                </Paragraph>
              ) : (
                <Text type="secondary">无</Text>
              )}
            </Card>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default LLMCallLogPage;
